import { Button, Input, Skeleton, Table } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import {
  flexRender,
  type RowSelectionState,
  type Table as TanStackTable,
} from "@tanstack/react-table";
import { AlertCircleIcon, ChevronLeftIcon, ChevronRightIcon, SearchXIcon } from "lucide-react";
import React from "react";
import { SearchInput as DataGridSearchInput } from "../search-input";
import { skeletonRow } from "./cells/styles";
import { SortableHeader } from "./sortable-header";

// ---------------------------------------------------------------------------
// GridStatus — shared between hooks and DataGrid.Content
// ---------------------------------------------------------------------------

export type GridStatus = "loading" | "error" | "empty" | "ready";

// ---------------------------------------------------------------------------
// Meta-driven class helpers
// ---------------------------------------------------------------------------

const metaClasses = (meta: Record<string, any> | undefined) =>
  cn(
    meta?.className,
    meta?.shrink && "w-0 whitespace-nowrap",
    meta?.align === "right" && "text-right",
    meta?.align === "center" && "text-center",
  );

// ---------------------------------------------------------------------------
// DataGrid compound component
// ---------------------------------------------------------------------------

type DataGridRootProps = React.ComponentProps<"div"> & {
  // Managed mode — provide table and DataGrid renders everything
  table?: TanStackTable<any>;
  status?: GridStatus;
  toolbar?: React.ReactNode;
  emptyState?: React.ReactNode;
  // Forwarded to Content
  loadingRows?: number;
  errorMessage?: string;
  errorContent?: React.ReactNode;
  emptyMessage?: string;
  emptyIcon?: React.ElementType;
  cellAlign?: "top" | "middle" | "bottom";
  // Forwarded to Pagination
  offset?: number;
  limit?: number;
  total?: number;
  filtered?: boolean;
  onPageChange?: (newOffset: number) => void;
  getPageHref?: (newOffset: number) => string;
  // Passthrough from useGrid (consumed by toolbar, stripped from DOM)
  q?: string;
  onSearchSubmit?: (value: string) => void;
  onSearchClear?: () => void;
  // Legacy compat
  onSearchChange?: (value: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowClick?: ((row: any) => void) | undefined;
  // Passthrough from useGrid selection — stripped from DOM
  rowSelection?: RowSelectionState;
  setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  selectedRows?: Array<unknown>;
};

const DataGridRoot = ({
  className,
  table,
  status,
  toolbar,
  emptyState,
  loadingRows,
  errorMessage,
  errorContent,
  emptyMessage,
  emptyIcon,
  cellAlign,
  offset,
  limit,
  total,
  filtered: _filtered,
  onPageChange,
  getPageHref,
  q: _q,
  onSearchSubmit: _onSearchSubmit,
  onSearchClear: _onSearchClear,
  onSearchChange: _onSearchChange,
  onRowClick,
  rowSelection: _rowSelection,
  setRowSelection: _setRowSelection,
  selectedRows: _selectedRows,
  children,
  ...props
}: DataGridRootProps) => (
  <div className={cn("rounded-md border", className)} {...props}>
    {status === "empty" && emptyState ? emptyState : table
      ? (
        <>
          {toolbar}
          <DataGridContent
            table={table}
            {...(status != null ? { status } : {})}
            {...(loadingRows != null ? { loadingRows } : {})}
            {...(errorMessage != null ? { errorMessage } : {})}
            errorContent={errorContent}
            {...(emptyMessage != null ? { emptyMessage } : {})}
            {...(emptyIcon != null ? { emptyIcon } : {})}
            {...(onRowClick != null ? { onRowClick } : {})}
            {...(cellAlign != null ? { cellAlign } : {})}
          />
          {total != null && offset != null && limit != null && onPageChange && (
            <DataGridPagination
              offset={offset}
              limit={limit}
              total={total}
              onPageChange={onPageChange}
              {...(getPageHref != null ? { getPageHref } : {})}
            />
          )}
        </>
      )
      : children}
  </div>
);

// ---------------------------------------------------------------------------
// DataGrid.Content
// ---------------------------------------------------------------------------

type DataGridContentProps<T> = {
  table: TanStackTable<T>;
  status?: GridStatus;
  loading?: boolean;
  loadingRows?: number;
  error?: boolean;
  errorMessage?: string;
  errorContent?: React.ReactNode;
  emptyMessage?: string;
  emptyIcon?: React.ElementType;
  className?: string;
  onRowClick?: (row: T) => void;
  cellAlign?: "top" | "middle" | "bottom";
};

const DataGridStatusBlock = ({
  icon: Icon,
  iconClassName,
  message,
}: { icon: React.ElementType; iconClassName?: string; message: string; }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-10">
    <Icon className={cn("size-8", iconClassName ?? "text-muted-foreground/50")} />
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

const DataGridContent = <T,>({
  table,
  status: statusProp,
  loading = false,
  loadingRows = 5,
  error = false,
  errorMessage = "Something went wrong.",
  errorContent,
  emptyMessage = "No data available.",
  emptyIcon,
  className,
  onRowClick,
  cellAlign,
}: DataGridContentProps<T>) => {
  const columns = table.getAllColumns();
  const status = statusProp ?? (loading ? "loading" : error ? "error" : "ready");

  if (status === "error") {
    return errorContent
      ? <div className="p-6">{errorContent}</div>
      : (
        <DataGridStatusBlock
          icon={AlertCircleIcon}
          iconClassName="text-destructive/60"
          message={errorMessage}
        />
      );
  }

  return (
    <Table
      className={cn(
        "w-full [&_th]:h-8 [&_th]:px-3 [&_th]:text-sm [&_td]:px-3 [&_td]:py-1.5 [&_td]:text-sm",
        cellAlign === "top" && "[&_td]:align-top",
        cellAlign === "bottom" && "[&_td]:align-bottom",
        className,
      )}
    >
      <Table.Header>
        {table.getHeaderGroups().map((headerGroup) => (
          <Table.Row key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const meta = header.column.columnDef.meta;
              const rendered = header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext());

              return (
                <Table.Head
                  key={header.id}
                  className={metaClasses(meta)}
                >
                  {header.column.getCanSort()
                    ? <SortableHeader column={header.column}>{rendered}</SortableHeader>
                    : rendered}
                </Table.Head>
              );
            })}
          </Table.Row>
        ))}
      </Table.Header>
      <Table.Body>
        {status === "loading"
          ? Array.from({ length: loadingRows }, (_, i) => (
            <Table.Row key={`skeleton-${i}`}>
              {columns.map((col, j) => (
                <Table.Cell key={`skeleton-${i}-${j}`} className={metaClasses(col.columnDef.meta)}>
                  <Skeleton className={cn(skeletonRow.action, "w-3/4")} />
                </Table.Cell>
              ))}
            </Table.Row>
          ))
          : table.getRowModel().rows.length !== 0
          ? table.getRowModel().rows.map((row) => (
            <Table.Row
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
              className={cn(onRowClick && "cursor-pointer")}
              onClick={onRowClick
                ? (e: React.MouseEvent) => {
                  if (
                    (e.target as HTMLElement).closest(
                      "a, button, [role='button'], input, select, textarea",
                    )
                  ) return;
                  onRowClick(row.original);
                }
                : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <Table.Cell key={cell.id} className={metaClasses(cell.column.columnDef.meta)}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          ))
          : (
            <Table.Row>
              <Table.Cell colSpan={columns.length}>
                <DataGridStatusBlock
                  icon={emptyIcon ?? SearchXIcon}
                  message={emptyMessage}
                />
              </Table.Cell>
            </Table.Row>
          )}
      </Table.Body>
    </Table>
  );
};

// ---------------------------------------------------------------------------
// DataGrid.Pagination
// ---------------------------------------------------------------------------

type DataGridPaginationProps = {
  offset: number;
  limit: number;
  total: number;
  onPageChange: (newOffset: number) => void;
  getPageHref?: (newOffset: number) => string;
  children?: React.ReactNode;
};

const DataGridPagination = (
  { offset, limit, total, onPageChange, getPageHref, children }: DataGridPaginationProps,
) => {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  const [pageInput, setPageInput] = React.useState(String(currentPage));
  React.useEffect(() => setPageInput(String(currentPage)), [currentPage]);

  if (total <= limit) return null;

  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;
  const rangeStart = offset + 1;
  const rangeEnd = Math.min(offset + limit, total);

  const commitPage = () => {
    const parsed = Number.parseInt(pageInput, 10);
    if (Number.isNaN(parsed) || parsed < 1 || parsed > totalPages || parsed === currentPage) {
      setPageInput(String(currentPage));
      return;
    }
    onPageChange((parsed - 1) * limit);
  };

  const prevOffset = Math.max(0, offset - limit);
  const nextOffset = offset + limit;

  const pageLink = (
    targetOffset: number,
    disabled: boolean,
    label: string,
    children: React.ReactNode,
  ) => {
    const cls = cn(Button.variants({ variant: "ghost", size: "icon" }), "size-7");
    if (disabled) {
      return (
        <span className={cn(cls, "pointer-events-none opacity-50")} aria-label={label}>
          {children}
        </span>
      );
    }
    const href = getPageHref?.(targetOffset);
    if (href) {
      return (
        <a
          href={href}
          className={cls}
          aria-label={label}
          onClick={(e) => {
            if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
              e.preventDefault();
              onPageChange(targetOffset);
            }
          }}
        >
          {children}
        </a>
      );
    }
    return (
      <button
        type="button"
        onClick={() => onPageChange(targetOffset)}
        className={cls}
        aria-label={label}
      >
        {children}
      </button>
    );
  };

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center border-t px-3 py-2">
      <span className="text-sm text-muted-foreground">
        Showing {rangeStart}–{rangeEnd} of {total}
      </span>

      <div className="flex items-center gap-1">
        {pageLink(prevOffset, !hasPrev, "Previous page", <ChevronLeftIcon className="size-4" />)}
        <Input
          type="text"
          inputMode="numeric"
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ""))}
          onBlur={commitPage}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          className="h-7 w-10 px-0 text-center text-sm tabular-nums"
          aria-label="Page number"
        />
        <span className="text-sm text-muted-foreground">of {totalPages}</span>
        {pageLink(nextOffset, !hasNext, "Next page", <ChevronRightIcon className="size-4" />)}
      </div>

      <div className="flex items-center justify-end gap-1">
        {children}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Assembled export
// ---------------------------------------------------------------------------

export const DataGrid = Object.assign(DataGridRoot, {
  Content: DataGridContent,
  Pagination: DataGridPagination,
  SearchInput: DataGridSearchInput,
});
